import {
  SignInPayload,
  SignInResponse,
  FindIdPayload,
  CheckIdDuplicatePayload,
  FindIdResponse,
  UpdatePasswordPayload,
  RequestPasswordVerificationCodePayload,
  CheckPasswordVerificationCodePayload,
  SignInAdminPayload,
  SignInAdminResponse,
} from '@dto';
import { AuthPort } from '@ports';
import { HttpInterface } from '@http';
import { AuthModel } from '@models';
import { AuthApiEndpoint, UserApiEndpoint } from '@api-endpoint';
import { AuthResponseAdapter } from '@adapters';
import { useCommonStore, useUserStore } from '@stores';
import axios from 'axios';
import { ROUTE_NAME } from '@enums';
import { useRouter } from 'vue-router';

type CommonStore = ReturnType<typeof useCommonStore>;
type UserStore = ReturnType<typeof useUserStore>;
export class AuthAdapter
  implements
    AuthPort<
      any,
      | SignInPayload
      | FindIdPayload
      | RequestPasswordVerificationCodePayload
      | CheckPasswordVerificationCodePayload
      | CheckIdDuplicatePayload
      | UpdatePasswordPayload
      | SignInAdminPayload
    >
{
  private router = useRouter();
  http: HttpInterface;
  commonStore: CommonStore;
  userStore: UserStore;
  constructor(httpInstance: HttpInterface) {
    this.http = httpInstance;
    this.commonStore = useCommonStore();
    this.userStore = useUserStore();
  }
  async signIn(payload: SignInPayload): Promise<AuthModel> {
    try {
      const response = await this.http.post<SignInResponse>(
        AuthApiEndpoint.signIn,
        payload
      );
      const authInfo = AuthResponseAdapter.signInAdapt(response.data);
      const authModel = AuthModel.createFromResponse(authInfo);
      const accessToken = authModel.getAccessToken();
      const membershipId = authModel.getMembershipId();
      this.commonStore.setAccessToken(accessToken, membershipId);
      this.userStore.setUser(accessToken);
      return authModel;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const errorCode = error.response.data.status;
        if (errorCode == 'NO_PASSWORD') {
          await this.router.push({ name: ROUTE_NAME.NewPasswordAccount });
        } else if (
          errorCode == 'ID_PASSWORD_MISMATCH_FORWARD_TO_PW_FIND_VIEW'
        ) {
          await this.router.push({ name: ROUTE_NAME.FindPassword });
        }
      }
      return AuthModel.createEmpty();
    }
  }
  async signOut(): Promise<boolean> {
    const response = await this.http.delete<boolean>(AuthApiEndpoint.signOut);
    this.commonStore.clearAccessToken();
    this.userStore.resetUser();
    return response.data;
  }
  async requestPasswordVerificationCode(
    payload: RequestPasswordVerificationCodePayload
  ): Promise<number | null> {
    const response = await this.http.post<number | null>(
      AuthApiEndpoint.requestPasswordVerificationCode,
      payload
    );
    return response.data;
  }
  async checkPasswordVerificationCode(
    payload: CheckPasswordVerificationCodePayload
  ): Promise<string | null> {
    const response = await this.http.post<string | null>(
      AuthApiEndpoint.checkPasswordVerificationCode,
      payload
    );
    return response.data;
  }
  async updatePassword(
    payload: UpdatePasswordPayload
  ): Promise<boolean | null> {
    const response = await this.http.put<boolean | null>(
      AuthApiEndpoint.updatePassword,
      payload
    );
    return response.data;
  }
  async findId(payload: FindIdPayload): Promise<string> {
    const response = await this.http.post<FindIdResponse>(
      AuthApiEndpoint.findId,
      payload
    ); // TODO: 이거 왜 post ??
    const authInfo = AuthResponseAdapter.findIdAdapt(response.data);
    return authInfo.id;
  }
  async checkIdDuplicate(
    payload: CheckIdDuplicatePayload
  ): Promise<boolean | null> {
    const response = await this.http.get<boolean | null>(
      UserApiEndpoint.checkUserIdDuplicate,
      payload
    );
    return response.data;
  }
  async signInAdmin(payload: SignInAdminPayload): Promise<SignInAdminResponse> {
    const response = await this.http.post<SignInAdminResponse>(
      AuthApiEndpoint.signInAdmin,
      payload
    );
    this.userStore.setUser(response.data.accessToken);
    this.commonStore.setAccessToken(response.data.accessToken);
    return response.data;
  }
}
